import React from 'react';
import { Link, useHistory } from 'react-router-dom';
import { Button, Table } from 'antd';
import { T, always, compose, cond, defaultTo, equals, isNil, prop, propOr, reject } from 'ramda';
import { isTrue } from 'ramda-adjunct';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Components } from '@fasstech/flexiget';
import DeleteGlobalVariableMutation from '../../../../_graphql/mutations/documents/DeleteGlobalVariableMutation';
import TableDates from '../tag/TableDates';
import { errorMessage, successMessage } from '../../../../utils/messageMutation';
import ConfirmButton from '../../../../components/ConfirmButton';
import useActionGuard from '../../../../hooks/useActionGuard';

const GlobalVariablesTable = ({ globalVariables, userCanEditOffer, offerId, reFetch }) => {
  const history = useHistory();
  const { loading, run } = useActionGuard();
  const deleteGlobalVariable = (globalVariableId, done) => {
    DeleteGlobalVariableMutation({ globalVariableId }, (ok, error) => {
      if (ok && !error) {
        successMessage('variable globale', 'supprimée', false, true);
        reFetch();
      } else {
        errorMessage();
      }
      done(ok, error);
    });
  };

  globalVariables = compose(
    reject(isNil),
    defaultTo([])
  )(globalVariables);

  const typeToFrench = (type) => cond([
    [equals('text'), always('Chaîne de caractères')],
    [equals('number'), always('Nombre')],
    [equals('boolean'), always('Booléen')],
    [equals(''), always('')],
    [T, () => errorMessage('Le type est inconnu')]
  ])(type);

  const linkToVariable = (variable) => cond([
    [() => isTrue(userCanEditOffer), always(`/offre/${offerId}/variables-globales/${variable.id}/edition`)],
    [T, always(`/offre/${offerId}/variables-globales/${variable.id}/preview`)]
  ])(offerId);

  return (
    <Table
      size="small"
      dataSource={globalVariables}
      rowKey="id"
      pagination={false}
      locale={{ emptyText: 'Aucune donnée' }}
      scroll={{ y: 520 }}
    >
      <Table.Column
        title="Nom"
        dataIndex="name"
        defaultSortOrder="ascend"
        width={300}
        sorter={(a, b) => prop('name', a).toLowerCase().localeCompare(prop('name', b).toLowerCase())}
        render={(name, variable) => (
          <Link to={linkToVariable(variable)}>
            {name}
          </Link>
        )
        }
      />

      <Table.Column
        title="Type"
        dataIndex="type"
        width={300}
        render={(type) => (
          <span className="capitalize">{typeToFrench(type)}</span>
        )}
      />

      {TableDates}

      <Table.Column
        title=""
        dataIndex="id"
        render={(globalVariableId, variable) => {
          const isUsed = propOr(false, 'isUsed', variable);
          if (userCanEditOffer) {
            return (
              <div className="flex justify-end gap-2 text-lg">
                <Components.Tooltip
                  title="Éditer la variable"
                >
                  <Button
                    icon={<EditOutlined className="hover:text-flexi-grey" />}
                    className="btn-edit-global-variable"
                    onClick={() => history.push(linkToVariable(variable)) }
                    type="text"
                  />
                </Components.Tooltip>

                <Components.Tooltip
                  title={isUsed
                    ? 'Cette variable est actuellement utilisée, vous ne pouvez pas la supprimer.'
                    : 'Supprimer la variable'}
                  className="btn-remove-global-variable"
                >
                  <ConfirmButton
                    title="Êtes-vous sûr de vouloir supprimer cette variable globale ?"
                    onConfirm={() => run(`delete-global-variable-${globalVariableId}`, (done) => deleteGlobalVariable(globalVariableId, done))}
                    disabled={isUsed}
                    icon={<DeleteOutlined className={`hover:text-flexi-red ${isUsed ? 'cursor-not-allowed' : 'cursor-pointer'}`} />}
                    buttonType="text"
                    loading={propOr(false, `delete-global-variable-${globalVariableId}`, loading)}
                  />
                </Components.Tooltip>
              </div>
            );
          }
          return null;
        }}
      />
    </Table>
  );
};

export default GlobalVariablesTable;
